import {getPersonalization} from "@/api/system/personalization";
import {listByIds} from "@/api/system/file";


const state = {
  // 个性化配置数据
  personalizationData: {},
  // Logo 图片 URL
  logoImg: "",
  // 轮播图图片 URL 数组
  carouselImages: [],
  // 是否已加载
  loaded: false,
  // 正在加载的 Promise，用于防止并发请求
  loadingPromise: null,
};

const mutations = {
  SET_PERSONALIZATION_DATA: (state, data) => {
    state.personalizationData = data;
  },
  SET_LOGO_IMG: (state, logoUrl) => {
    state.logoImg = logoUrl;
  },
  SET_CAROUSEL_IMAGES: (state, images) => {
    state.carouselImages = images;
  },
  SET_LOADED: (state, loaded) => {
    state.loaded = loaded;
  },
  SET_LOADING_PROMISE: (state, promise) => {
    state.loadingPromise = promise;
  },
};

const getters = {
  personalizationData: (state) => state.personalizationData,
  logoImg: (state) => state.logoImg,
  // 获取网站标题，如果没有则返回默认值
  websiteTitle: (state) => {
    return state.personalizationData?.websiteTitle || "技能图书馆";
  },
  // 获取学校名称
  schoolName: (state) => {
    return state.personalizationData?.schoolName || "";
  },
  // 获取 Logo，如果没有则返回默认图片路径
  // 只有在加载完成且没有图片时才返回默认图片
  displayLogo: (state) => {
    // 如果还在加载中，返回 null（表示应该显示 loading）
    if (!state.loaded) {
      return null;
    }
    // 如果加载完成且有图片，返回图片URL
    if (state.logoImg && state.logoImg.trim() !== "") {
      return state.logoImg;
    }
    // 如果加载完成但没有图片，返回默认图片
    return require("@/assets/logo-2.png");
  },
  // 判断 Logo 是否正在加载
  isLogoLoading: (state) => {
    return !state.loaded;
  },
  // 获取轮播图图片列表，如果没有数据则返回默认图片
  carouselImages: (state) => {
    if (state.carouselImages && state.carouselImages.length > 0) {
      return state.carouselImages;
    }
    // 如果没有数据，返回默认图片数组
    return [require("@/assets/index/index-15.png")];
  },
  loaded: (state) => state.loaded,
};

const actions = {
  // 加载个性化配置数据
  // 支持未登录情况，如果接口需要登录，会优雅处理错误
  loadPersonalizationData({ commit, state }) {
    // 如果已经加载过，直接返回
    if (state.loaded) {
      return Promise.resolve(state.personalizationData);
    }

    // 如果正在加载中，返回现有的 Promise，避免重复请求
    if (state.loadingPromise) {
      return state.loadingPromise;
    }

    const personalizationQuery = [
      "schoolName",
      "phone",
      "icpRecord",
      "publicSecurity",
      "logCover",
      "websiteTitle",
      "carouselImages"
    ];

    // 创建请求 Promise 并缓存
    const requestPromise = getPersonalization({
      keyList: personalizationQuery.toString(),
    })
      .then((res) => {
        const personalizationData = res.data || {};

        // 收集需要加载的图片任务
        const loadTasks = [];

        // 如果有 logCover，加载 Logo 图片
        const logCoverId = personalizationData.logCover;
        if (logCoverId) {
          loadTasks.push(
            listByIds(logCoverId)
              .then((fileRes) => {
                if (fileRes.data && fileRes.data.length > 0) {
                  commit("SET_LOGO_IMG", fileRes.data[0].fileUrl);
                }
              })
              .catch((error) => {
                console.error("加载 Logo 失败:", error);
              })
          );
        }

        // carouselImages 现在直接返回包含 URL 的 JSON 字符串，不需要再调用 listByIds 接口
        // 数据已经在 personalizationData 中，由组件直接处理

        // 等待所有图片加载任务完成（无论成功与否）后，才标记为已加载
        if (loadTasks.length > 0) {
          return Promise.all(loadTasks)
            .then(() => {
              commit("SET_PERSONALIZATION_DATA", personalizationData);
              commit("SET_LOADED", true);
              commit("SET_LOADING_PROMISE", null); // 清除加载中的 Promise
              return personalizationData;
            })
            .catch(() => {
              // 即使有任务失败，也标记为已加载
              commit("SET_PERSONALIZATION_DATA", personalizationData);
              commit("SET_LOADED", true);
              commit("SET_LOADING_PROMISE", null); // 清除加载中的 Promise
              return personalizationData;
            });
        }

        // 如果没有需要加载的图片，直接标记为已加载
        commit("SET_PERSONALIZATION_DATA", personalizationData);
        commit("SET_LOADED", true);
        commit("SET_LOADING_PROMISE", null); // 清除加载中的 Promise
        return personalizationData;
      })
      .catch((error) => {
        // 优雅处理错误：可能是未登录、网络错误等
        // 不抛出错误，避免影响页面正常显示
        console.warn("加载个性化配置失败（可能是未登录或网络问题）:", error);
        commit("SET_LOADED", true); // 即使失败也标记为已加载，避免重复请求
        commit("SET_LOADING_PROMISE", null); // 清除加载中的 Promise
        return {};
      });

    // 缓存这个 Promise
    commit("SET_LOADING_PROMISE", requestPromise);
    return requestPromise;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};

